<script>

import moment from 'moment';
import { times } from 'lodash-es';
import Vue from 'vue';

export default {
    props: {
        startDate: {
            type: String,
            default: moment().format('YYYY-MM-DD')
        },
        value: {
            type: Object,
            required: true
        },
        heading: {
            type: String,
            default: 'Select a date'
        },
        label: {
            type: String,
            required: true
        },
    },
    data () {
        return {
            open: false,
            internalValue: Vue.util.extend({}, this.value),
            currentMonth: moment(this.startDate, 'YYYY-MM-DD').month(),
            currentYear: moment(this.startDate, 'YYYY-MM-DD').year(),
            nextMonth: moment(this.startDate, 'YYYY-MM-DD').month() !== 11 ? moment(this.startDate, 'YYYY-MM-DD').month() + 1 : 0,
            nextYear: moment(this.startDate, 'YYYY-MM-DD').month() !== 11 ? moment(this.startDate, 'YYYY-MM-DD').year() : moment(this.startDate, 'YYYY-MM-DD').year() + 1,
        }
    },
    computed: {
        weekdayNames () {
            let days = [];
            let date = moment(this.currentMonth + 1, 'M').startOf('isoWeek');

            times(7, () => {
                days.push(date.format('dd').substr(0, 1));
                date.add(1, 'day');
            });

            return days;
        },
        currentMonthDays () {
            let days = [];
            let date = moment(`${this.currentYear}-${this.currentMonth + 1}`, 'YYYY-M').startOf('isoWeek');

            times(42, () => {
                days.push({
                    timestamp: date.format('YYYY-MM-DD'),
                    day: date.format('D'),
                    month: date.month(),
                    date: date,
                    inMonth: date.month() === this.currentMonth
                });
                date.add(1, 'day');
            });

            return days;
        },
        nextMonthDays () {
            let days = [];
            let date = moment(`${this.nextYear}-${this.nextMonth + 1}`, 'YYYY-M').startOf('isoWeek');

            times(42, () => {
                days.push({
                    timestamp: date.format('YYYY-MM-DD'),
                    day: date.format('D'),
                    month: date.month(),
                    date: date,
                    inMonth: date.month() === this.nextMonth
                });
                date.add(1, 'day');
            });

            return days;
        },
        currentMonthName () {
            return moment(this.currentMonth + 1, 'M').format('MMMM');
        },
        nextMonthName () {
            return moment(this.nextMonth + 1, 'M').format('MMMM');
        }
    },
    watch: {
        value () {
            this.internalValue.from = this.value.from;
            this.internalValue.to = this.value.to;
        }
    },
    methods: {
        selectDate (date) {
            if (this.internalValue.from && this.internalValue.to) {
                this.internalValue.to = undefined;
                this.internalValue.from = date.timestamp;
            } else if (!this.internalValue.from) {
                this.internalValue.from = date.timestamp;
            } else {
                if (this.internalValue.from > date.timestamp) {
                    this.internalValue.to = this.internalValue.from;
                    this.internalValue.from = date.timestamp;
                } else {
                    this.internalValue.to = date.timestamp;
                }
                this.syncMonth();
                this.update();
            }
        },
        update () {
            this.syncMonth();
            this.$emit('input', {
                from: this.internalValue.from,
                to: this.internalValue.to
            });
            this.open = false;
        },
        prev () {
            if (this.currentMonth === 0) {
                this.currentMonth = 11;
                this.currentYear -= 1;
            } else {
                this.currentMonth -= 1;
            }

            if (this.nextMonth === 0) {
                this.nextMonth = 11;
                this.nextYear -= 1;
            } else {
                this.nextMonth -= 1;
            }
        },
        next () {
            if (this.currentMonth === 11) {
                this.currentMonth = 0;
                this.currentYear += 1;
            } else {
                this.currentMonth += 1;
            }
            if (this.nextMonth === 11) {
                this.nextMonth = 0;
                this.nextYear += 1;
            } else {
                this.nextMonth += 1;
            }
        },
        syncMonth () {
            // If from and to dates are within the same month the current month should be the selected month
            this.currentMonth = moment(this.internalValue.from).month();
            this.currentYear = moment(this.internalValue.from).year();

            if (this.currentMonth + 1 === 12) {
                this.nextMonth = 0;
                this.nextYear = this.currentYear + 1;
            } else {
                this.nextMonth = this.currentMonth + 1;
                this.nextYear = this.currentYear;
            }
        },
        hide ($event) {
            if ($event.target.id === 'date-picker-container') {
                if (!this.internalValue.to) {
                    this.internalValue.to = this.internalValue.from;
                    this.update();
                }
                this.open = false;
            }
        },
        setThisDay () {
            this.internalValue.from = moment().format('YYYY-MM-DD');
            this.internalValue.to = moment().format('YYYY-MM-DD');
            this.update();
        },
        setThisWeek () {
            this.internalValue.from = moment().startOf('isoWeek').format('YYYY-MM-DD');
            this.internalValue.to = moment().endOf('isoWeek').format('YYYY-MM-DD');
            this.update();
        },
        setThisMonth () {
            this.internalValue.from = moment().startOf('month').format('YYYY-MM-DD');
            this.internalValue.to = moment().endOf('month').format('YYYY-MM-DD');
            this.update();
        },
        setThisYear () {
            this.internalValue.from = moment().startOf('year').format('YYYY-MM-DD');
            this.internalValue.to = moment().endOf('year').format('YYYY-MM-DD');
            this.update();
        },
        setLastDay () {
            this.internalValue.from = moment().subtract(1, 'days').format('YYYY-MM-DD');
            this.internalValue.to = moment().subtract(1, 'days').format('YYYY-MM-DD');
            this.update();
        },
        setLastWeek () {
            this.internalValue.from = moment().subtract(1, 'weeks').startOf('isoWeek').format('YYYY-MM-DD');
            this.internalValue.to = moment().subtract(1, 'weeks').endOf('isoWeek').format('YYYY-MM-DD');
            this.update();
        },
        setLastMonth () {
            this.internalValue.from = moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
            this.internalValue.to = moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD');
            this.update();
        },
        setLastYear () {
            this.internalValue.from = moment().subtract(1, 'years').startOf('year').format('YYYY-MM-DD');
            this.internalValue.to = moment().subtract(1, 'years').endOf('year').format('YYYY-MM-DD');
            this.update();
        }
    }
}
</script>
<template>
    <div class="date-picker">
        <div class="date-picker__option" @click="open = true">
            <div class="date-picker__option-label">{{ label }}</div>
            <div class="date-picker__option-value">
                <span v-if="internalValue.from">{{ [internalValue.from, "YYYY-MM-DD"] | moment("DD/MM/YYYY") }}</span>
                <span v-if="internalValue.to">- {{ [internalValue.to, "YYYY-MM-DD"] | moment("DD/MM/YYYY") }}</span>
            </div>
        </div>
        <div v-if="open" id="date-picker-container" class="date-picker__container" @click="hide($event)">
            <div class="date-picker__body">
                <div class="date-picker__header">
                    {{ heading }}
                </div>
                <div class="date-picker__calendars">
                    <div class="date-picker__calendar">
                        <div class="date-picker__controls">
                            <div class="date-picker__button date-picker__button--prev" @click="prev">
                                <i class="fa fa-fw fa-angle-left"/>
                            </div>
                            <div class="date-picker__description">
                                {{ currentMonthName }}, {{ currentYear }}
                            </div>
                            <div class="date-picker__button date-picker__button--next" @click="next">
                                <i class="fa fa-fw fa-angle-right"/>
                            </div>
                        </div>
                        <div class="date-picker__weekdays">
                            <div v-for="(weekday, index) in weekdayNames" :key="index" class="date-picker__weekday">{{ weekday }}</div>
                        </div>
                        <div class="date-picker__month">
                            <div v-for="date in currentMonthDays"
                                 :key="date.timestamp"
                                 :class="{
                                     'date-picker__date--selected': date.timestamp >= internalValue.from && date.timestamp <= internalValue.to || date.timestamp === internalValue.from && !internalValue.to,
                                     'date-picker__date--out': !date.inMonth && !(date.timestamp >= internalValue.from && date.timestamp <= internalValue.to || date.timestamp === internalValue.from && !internalValue.to),
                                     'date-picker__date--outselect': !date.inMonth && (date.timestamp >= internalValue.from && date.timestamp <= internalValue.to || date.timestamp === internalValue.from && !internalValue.to)}"
                                 class="date-picker__date"
                                 @click="selectDate(date)">
                                {{ date.day }}
                            </div>
                        </div>
                    </div>
                    <div class="date-picker__calendar">
                        <div class="date-picker__controls">
                            <div class="date-picker__button date-picker__button--prev" @click="prev">
                                <i class="fa fa-fw fa-angle-left"/>
                            </div>
                            <div class="date-picker__description">
                                {{ nextMonthName }}, {{ nextYear }}
                            </div>
                            <div class="date-picker__button date-picker__button--next" @click="next">
                                <i class="fa fa-fw fa-angle-right"/>
                            </div>
                        </div>
                        <div class="date-picker__weekdays">
                            <div v-for="(weekday, index) in weekdayNames" :key="index" class="date-picker__weekday">{{ weekday }}</div>
                        </div>
                        <div class="date-picker__month">
                            <div v-for="date in nextMonthDays"
                                 :key="date.timestamp"
                                 :class="{
                                     'date-picker__date--selected': date.timestamp >= internalValue.from && date.timestamp <= internalValue.to || date.timestamp === internalValue.from && !internalValue.to,
                                     'date-picker__date--out': !date.inMonth && !(date.timestamp >= internalValue.from && date.timestamp <= internalValue.to || date.timestamp === internalValue.from && !internalValue.to),
                                     'date-picker__date--outselect': !date.inMonth && (date.timestamp >= internalValue.from && date.timestamp <= internalValue.to || date.timestamp === internalValue.from && !internalValue.to)}"
                                 class="date-picker__date"
                                 @click="selectDate(date)">
                                {{ date.day }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="date-picker__predefined">
                    <a class="date-picker__predefined-link" @click="setLastDay">{{ $t('datePicker.predefinedLinks.yesterday') }}</a>
                    <a class="date-picker__predefined-link" @click="setLastWeek">{{ $t('datePicker.predefinedLinks.lastWeek') }}</a>
                    <a class="date-picker__predefined-link" @click="setLastMonth">{{ $t('datePicker.predefinedLinks.lastMonth') }}</a>
                    <a class="date-picker__predefined-link" @click="setLastYear">{{ $t('datePicker.predefinedLinks.lastYear') }}</a>
                    <a class="date-picker__predefined-link" @click="setThisDay">{{ $t('datePicker.predefinedLinks.today') }}</a>
                    <a class="date-picker__predefined-link" @click="setThisWeek">{{ $t('datePicker.predefinedLinks.thisWeek') }}</a>
                    <a class="date-picker__predefined-link" @click="setThisMonth">{{ $t('datePicker.predefinedLinks.thisMonth') }}</a>
                    <a class="date-picker__predefined-link" @click="setThisYear">{{ $t('datePicker.predefinedLinks.thisYear') }}</a>
                </div>
            </div>
        </div>
    </div>

</template>



// WEBPACK FOOTER //
// src/components/DatePicker/DatePicker.vue